import type { ActionContext } from 'js/lib/ActionContext';

import type { CourseData, CourseResponseTypeMetadata } from 'bundles/course-v2/hooks/useCourseBySlug';
import CourseStoreClass from 'bundles/in-course-stores/stores/CourseStore';
import { CourseType, type CourseTypeMetadata } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';

type CourseIdentifiers = {
  courseId: string;
  courseSlug: string;
  courseTypeMetadata?: CourseTypeMetadata | null;
};

/**
 * Converts CourseResponseTypeMetadata to CourseTypeMetadata format
 */
const mapToTypeMetadata = (responseMetadata: CourseResponseTypeMetadata | null): CourseTypeMetadata | null => {
  if (!responseMetadata) {
    return null;
  }

  // Map __typename to CourseType enum value
  let typeName = CourseType.STANDARD_COURSE;
  const typeNameStr = responseMetadata.metadata.__typename?.toLowerCase() || '';

  if (typeNameStr.includes(CourseType.RHYME_PROJECT.toLocaleLowerCase())) {
    typeName = CourseType.RHYME_PROJECT;
  } else if (typeNameStr.includes(CourseType.PROJECT.toLocaleLowerCase())) {
    typeName = CourseType.PROJECT;
  } else if (typeNameStr.includes(CourseType.VERIFIED_SKILLS_COURSE.toLocaleLowerCase())) {
    typeName = CourseType.VERIFIED_SKILLS_COURSE;
  }

  return {
    typeName,
    definition: {},
  };
};

/**
 * Handles setting course identifiers in the store.
 * This doesn't actually fetch data - it's designed to work with withCourseBySlug HOC that already fetched the data.
 * Serves as a drop-in replacement for loadCourseV1 while using GraphQL
 */
const loadCourseBySlug = (
  actionContext: ActionContext,
  courseData: {
    courseSlug: string;
    course: CourseData | null;
  }
): Promise<CourseIdentifiers | null> => {
  const { course, courseSlug } = courseData;
  const courseCertificates: Array<string> = [];

  // Check if course identifiers are already loaded - if so, don't reload
  if (actionContext.getStore(CourseStoreClass).haveCourseIdentifiersLoaded()) {
    // Return existing data from the store
    const courseId = actionContext.getStore(CourseStoreClass).getCourseId();
    const courseTypeMetadata = actionContext.getStore(CourseStoreClass).getMetadata()?.courseTypeMetadata || {};

    return Promise.resolve({
      courseId,
      courseSlug,
      courseCertificates,
      courseTypeMetadata,
    });
  }

  if (course) {
    const courseId = course.courseId;
    const courseTypeMetadata = mapToTypeMetadata(course.courseTypeMetadata);

    actionContext.dispatch('SET_COURSE_IDENTIFIERS', {
      courseId,
      courseSlug,
      courseCertificates,
      courseTypeMetadata,
    });

    return Promise.resolve({
      courseId,
      courseSlug,
      courseCertificates,
      courseTypeMetadata,
    });
  }

  // If no courseData provided, dispatch empty data for now
  // This allows the component to continue rendering while waiting for GraphQL data
  return Promise.resolve(null);
};

export default loadCourseBySlug;
